/**
 * 项目（文件夹）管理 API — Task 9 / design.md D1/D8
 *
 * Project 仅为分组文件夹；文件/状态/指派见 WorkItem API（work-item.ts）。
 */
import type { AxiosRequestConfig } from 'axios'

import { ProjectType, ProjectStatus, TaskAssignmentType, TaskType } from '@/api/common-types'
import type {
  ActivityLog,
  PaginatedResponse,
  PaginationParams,
  UserSummary,
} from '@/api/common-types'
import type { BatchRequestConfig } from '@/shared/api/use-api-batch'
import { request } from '@/shared/api/request'

export { ProjectType, ProjectStatus, TaskAssignmentType, TaskType }

type RequestConfig = AxiosRequestConfig | BatchRequestConfig

/** 项目文件夹（与后端 ProjectResponse 对齐） */
export interface Project {
  id: number
  title: string
  creator_id: number
  created_at: string
  updated_at: string
  /** @deprecated 文件夹不再存储类型，见 WorkItem.item_type */
  project_type?: ProjectType
  /** @deprecated 文件夹状态由 WorkItem 聚合 */
  status?: ProjectStatus
  /** @deprecated */
  task_type?: TaskAssignmentType | TaskType
  /** @deprecated */
  assignee_id?: number
  creator?: UserSummary
  assignee?: UserSummary
  /**
   * 过渡期：旧 ProjectDetail / my-projects 仍读已下沉到 WorkItem 的字段。
   * Section 10 重写页面后删除此索引签名。
   */
  [key: string]: unknown
}

export type ProjectListItem = Project

export interface ProjectCreateRequest {
  title: string
}

export interface ProjectUpdateRequest {
  title: string
}

export interface ProjectListResponse extends PaginatedResponse<ProjectListItem> {
  has_next: boolean
  has_prev: boolean
}

export interface ProjectResolveTitleRequest {
  title: string
  intent?: 'join' | 'create'
  force_create?: boolean
}

export interface ProjectResolveTitleResponse {
  action: 'join' | 'create' | 'duplicate_prompt'
  project_id?: number | null
  normalized_title?: string | null
  duplicate?: boolean | null
  existing_project_id?: number | null
  suggested_title?: string | null
}

export interface ProjectQueryParams extends PaginationParams {
  search?: string
  search_keyword?: string
  sort_by?: string
  sort_order?: 'asc' | 'desc'
  /** @deprecated */
  project_type?: ProjectType
  /** @deprecated */
  status?: ProjectStatus
  /** @deprecated */
  task_type?: TaskAssignmentType | TaskType
  /** @deprecated */
  creator_id?: number
  /** @deprecated */
  assignee_id?: number
}

/** @deprecated 项目文件夹不再有独立统计端点，见 dashboard / WorkItem 聚合 */
export interface ProjectStatistics {
  total_projects: number
  video_projects: number
  document_projects: number
  draft_projects: number
  pending_projects: number
  processing_projects: number
  completed_projects: number
  failed_projects: number
  my_projects: number
  assigned_projects: number
}

export interface ProjectActivity extends ActivityLog {
  project_id: number
}

export interface ProjectPermission {
  id: number
  project_id: number
  user_id: number
  permission_type: string
  granted_by: number
  granted_at: string
  created_at: string
  user?: UserSummary
  granter?: UserSummary
}

function normalizeProjectList(
  response: unknown,
  params?: ProjectQueryParams,
): ProjectListResponse {
  if (Array.isArray(response)) {
    return {
      items: response as Project[],
      total: response.length,
      page: params?.page || 1,
      page_size: params?.page_size || params?.limit || 20,
      has_next: false,
      has_prev: false,
    }
  }

  if (response && typeof response === 'object') {
    const normalized = response as Record<string, unknown>
    const items = Array.isArray(normalized.items)
      ? (normalized.items as Project[])
      : Array.isArray(normalized.results)
        ? (normalized.results as Project[])
        : []
    return {
      items,
      total: typeof normalized.total === 'number' ? normalized.total : items.length,
      page: (normalized.page as number) || params?.page || 1,
      page_size:
        (normalized.page_size as number) ||
        (normalized.limit as number) ||
        params?.page_size ||
        params?.limit ||
        20,
      has_next: Boolean(normalized.has_next),
      has_prev: Boolean(normalized.has_prev),
    }
  }

  return {
    items: [],
    total: 0,
    page: params?.page || 1,
    page_size: params?.page_size || params?.limit || 20,
    has_next: false,
    has_prev: false,
  }
}

function deprecatedProjectApi(name: string): never {
  throw new Error(
    `${name} 已废弃：请改用 projectApi（文件夹 CRUD）与 workItemApi（条目 CRUD）。`,
  )
}

export async function getProjects(
  params?: ProjectQueryParams,
): Promise<ProjectListResponse> {
  const response = await request.get<unknown>('/projects/', { params })
  return normalizeProjectList(response, params)
}

export async function getProject(
  id: number,
  config?: RequestConfig,
): Promise<Project> {
  return request.get<Project>(`/projects/${id}`, config)
}

export async function createProject(data: ProjectCreateRequest): Promise<Project> {
  return request.post<Project>('/projects/', data)
}

export async function updateProject(
  id: number,
  data: ProjectUpdateRequest,
): Promise<Project> {
  return request.put<Project>(`/projects/${id}`, data)
}

export async function deleteProject(id: number): Promise<void> {
  await request.delete(`/projects/${id}`)
}

export async function resolveProjectTitle(
  data: ProjectResolveTitleRequest,
): Promise<ProjectResolveTitleResponse> {
  return request.post<ProjectResolveTitleResponse>('/projects/resolve-title', data)
}

/** @deprecated 使用 resolveProjectTitle */
export async function getMyProjects(
  params?: ProjectQueryParams,
): Promise<PaginatedResponse<Project>> {
  return getProjects(params)
}

/** @deprecated 使用 getProjects + WorkItem 范围过滤 */
export async function getAssignedProjects(
  params?: ProjectQueryParams,
): Promise<PaginatedResponse<Project>> {
  return getProjects(params)
}

/* eslint-disable @typescript-eslint/no-unused-vars -- 废弃签名保留参数名供调用方对照 */
export async function batchDeleteProjects(_ids: number[]): Promise<void> {
  deprecatedProjectApi('batchDeleteProjects')
}

export async function assignProject(_id: number, _assigneeId: number): Promise<Project> {
  deprecatedProjectApi('assignProject')
}

export async function unassignProject(_id: number): Promise<Project> {
  deprecatedProjectApi('unassignProject')
}

export async function updateProjectStatus(
  _id: number,
  _data: { status: ProjectStatus; message?: string },
): Promise<Project> {
  deprecatedProjectApi('updateProjectStatus')
}

export async function assignProjectTask(
  _id: number,
  _data: {
    assignee_id: number
    task_type: TaskAssignmentType | TaskType
    deadline?: string
    message?: string
  },
): Promise<{ message: string; task_id: number }> {
  deprecatedProjectApi('assignProjectTask')
}

export async function completeProjectTask(
  _id: number,
  _message?: string,
): Promise<{ message: string }> {
  deprecatedProjectApi('completeProjectTask')
}

export async function getProjectStatistics(): Promise<ProjectStatistics> {
  deprecatedProjectApi('getProjectStatistics')
}

export async function getProjectPermissions(
  _id: number,
): Promise<ProjectPermission[]> {
  deprecatedProjectApi('getProjectPermissions')
}

export async function searchProjects(_params: {
  keyword: string
  search_fields?: string[]
  project_type?: ProjectType
  status?: ProjectStatus
  limit?: number
}): Promise<ProjectListItem[]> {
  deprecatedProjectApi('searchProjects')
}

export async function getRecentActivities(_params?: {
  days?: number
  limit?: number
}): Promise<ProjectActivity[]> {
  deprecatedProjectApi('getRecentActivities')
}

export async function batchUpdateProjectStatus(
  _projectIds: number[],
  _status: ProjectStatus,
): Promise<{ message: string }> {
  deprecatedProjectApi('batchUpdateProjectStatus')
}

export async function exportProjectReport(
  _projectId: number,
  _format: 'html' | 'json' = 'html',
): Promise<{ download_url: string }> {
  deprecatedProjectApi('exportProjectReport')
}

export async function uploadVideoFile(
  _projectId: number,
  _file: File,
  _onProgress?: (progress: number) => void,
  _signal?: AbortSignal,
): Promise<unknown> {
  deprecatedProjectApi('uploadVideoFile — 请改用 videoApi.uploadVideo(workItemId)')
}

export async function extractAudioFromVideo(_projectId: number): Promise<unknown> {
  deprecatedProjectApi('extractAudioFromVideo')
}

export async function getVideoInfo(_projectId: number): Promise<unknown> {
  deprecatedProjectApi('getVideoInfo')
}
/* eslint-enable @typescript-eslint/no-unused-vars */

export async function getProjectActivities(
  id: number,
  params?: PaginationParams,
): Promise<PaginatedResponse<ActivityLog>> {
  return request.get<PaginatedResponse<ActivityLog>>(`/projects/${id}/activities`, {
    params,
  })
}

export const projectApi = {
  getProjects,
  getProject,
  createProject,
  updateProject,
  deleteProject,
  resolveProjectTitle,
  getProjectActivities,
  getMyProjects,
  getAssignedProjects,
  batchDeleteProjects,
  assignProject,
  assignProjectTask,
  unassignProject,
  updateProjectStatus,
  completeProjectTask,
  getProjectStatistics,
  getProjectPermissions,
  searchProjects,
  getRecentActivities,
  batchUpdateProjectStatus,
  exportProjectReport,
  uploadVideoFile,
  extractAudioFromVideo,
  getVideoInfo,
}

export default projectApi
